/**
 * EPIC290 Phase 4 — Review Queue fetch/fallback policy.
 *
 * Under DB pressure, do not cascade persisted (≤30s) → legacy (another ≤30s).
 * Prefer preserving last usable rows + explicit degradation over a false empty queue.
 *
 * The persisted grouped response is authoritative for review-queue membership.
 * A narrower live refresh (prefer_persisted=false) may enrich the same group
 * identity, but an empty or partial live body must not drop persisted patterns.
 */

export type ReviewQueueFailureKind =
  | 'query_timeout'
  | 'backend_degraded'
  | 'network'
  | 'unavailable'
  | 'unknown';

/** Primary persisted groups request budget (aligned under SPA 30s / DB statement_timeout 20s). */
export const REVIEW_QUEUE_PERSISTED_TIMEOUT_MS = 20_000;

/** Bounded legacy/live fallback budget — never another full 30s cascade. */
export const REVIEW_QUEUE_FALLBACK_TIMEOUT_MS = 8_000;

/** Deferred live refresh after a successful persisted paint. */
export const REVIEW_QUEUE_LIVE_REFRESH_TIMEOUT_MS = 8_000;

export function classifyReviewQueueFailure(error: unknown): ReviewQueueFailureKind {
  const anyErr = error as {
    code?: string;
    message?: string;
    response?: { status?: number; data?: { error?: string; detail?: string } };
  } | null;

  const status = anyErr?.response?.status;
  const apiError = String(anyErr?.response?.data?.error || '').toLowerCase();
  const detail = String(anyErr?.response?.data?.detail || '').toLowerCase();
  const message = String(anyErr?.message || '').toLowerCase();
  const code = String(anyErr?.code || '').toUpperCase();

  if (
    code === 'ECONNABORTED'
    || message.includes('timeout')
    || apiError.includes('timeout')
    || detail.includes('statement timeout')
    || detail.includes('canceling statement')
    || status === 408
  ) {
    return 'query_timeout';
  }

  if (
    status === 503
    || status === 429
    || status === 502
    || status === 504
    || apiError === 'db_capacity_exhausted'
    || apiError.includes('backend_degraded')
    || detail.includes('pool_acquire_timeout')
    || detail.includes('db_unreachable')
  ) {
    return 'backend_degraded';
  }

  // No HTTP response → transport/network loss (distinct from backend overload).
  if (status == null && (code === 'ERR_NETWORK' || message.includes('network') || Boolean(error))) {
    // Prefer network when axios reports no response; keep unknown for empty objects.
    if (code === 'ERR_NETWORK' || message.includes('network error') || message.includes('failed to fetch')) {
      return 'network';
    }
    if (status == null && message && !apiError) {
      return 'network';
    }
  }

  if (
    status === 404
    || apiError.includes('persisted')
    || apiError.includes('not_available')
    || apiError.includes('unavailable')
  ) {
    return 'unavailable';
  }

  if (status != null && status >= 500) {
    return 'backend_degraded';
  }

  if (status == null) {
    return 'network';
  }

  return 'unknown';
}

/**
 * Only allow a short legacy/live fallback for non-pressure "unavailable" shapes.
 * Timeouts and capacity errors must not start a second heavy groups query.
 */
export function shouldAttemptLegacyGroupsFallback(kind: ReviewQueueFailureKind): boolean {
  return kind === 'unavailable' || kind === 'unknown';
}

export type ReviewQueueGroupRow = {
  group_key?: string | null;
  pattern_key?: string | null;
  incident_id?: string | null;
  last_seen_at?: string | null;
  last_seen?: string | null;
  updated_at?: string | null;
  alert_count?: number | null;
  evidence_count?: number | null;
  recent_alert_count?: number | null;
  recent_active_days?: number | null;
};

const FRESHNESS_TIMESTAMP_FIELDS = ['last_seen_at', 'last_seen', 'updated_at'] as const;
const FRESHNESS_COUNT_FIELDS = [
  'alert_count',
  'evidence_count',
  'recent_alert_count',
  'recent_active_days',
] as const;

/**
 * Stable review-queue identity. group_key wins so the same pattern is not
 * duplicated when a live row also carries the parent incident id.
 */
export function incidentGroupIdentity(
  group: Pick<ReviewQueueGroupRow, 'group_key' | 'pattern_key' | 'incident_id'> | null | undefined,
): string {
  const groupKey = String(group?.group_key || '').trim();
  if (groupKey) return `group:${groupKey}`;
  const patternKey = String(group?.pattern_key || '').trim();
  if (patternKey) return `pattern:${patternKey}`;
  const incidentId = String(group?.incident_id || '').trim();
  if (incidentId) return `incident:${incidentId}`;
  return '';
}

function laterTimestamp(left: unknown, right: unknown): string | undefined {
  const leftText = typeof left === 'string' ? left.trim() : '';
  const rightText = typeof right === 'string' ? right.trim() : '';
  const leftMs = Date.parse(leftText);
  const rightMs = Date.parse(rightText);
  if (Number.isFinite(leftMs) && Number.isFinite(rightMs)) {
    return rightMs > leftMs ? rightText : leftText;
  }
  if (Number.isFinite(rightMs)) return rightText;
  if (Number.isFinite(leftMs)) return leftText;
  return undefined;
}

function maxCount(left: unknown, right: unknown): number | undefined {
  const leftCount = typeof left === 'number' && Number.isFinite(left) ? left : null;
  const rightCount = typeof right === 'number' && Number.isFinite(right) ? right : null;
  if (leftCount != null && rightCount != null) return Math.max(leftCount, rightCount);
  if (leftCount != null) return leftCount;
  if (rightCount != null) return rightCount;
  return undefined;
}

function mergeGroupedReviewFields<T extends ReviewQueueGroupRow>(persisted: T, live: T): T {
  const merged: T = { ...persisted };
  const target = merged as ReviewQueueGroupRow;
  const persistedRow = persisted as ReviewQueueGroupRow;
  const liveRow = live as ReviewQueueGroupRow;
  for (const key of FRESHNESS_TIMESTAMP_FIELDS) {
    const next = laterTimestamp(persistedRow[key], liveRow[key]);
    if (next) target[key] = next;
  }
  for (const key of FRESHNESS_COUNT_FIELDS) {
    const next = maxCount(persistedRow[key], liveRow[key]);
    if (next != null) target[key] = next;
  }
  return merged;
}

/**
 * Persisted membership is authoritative. Live rows may refresh freshness on a
 * matching identity and must not add, drop, or duplicate patterns.
 * An empty live list leaves the persisted rows unchanged. An empty persisted
 * list stays empty so a genuinely empty authoritative response can clear the queue.
 */
export function mergeAuthoritativePersistedGroups<T extends ReviewQueueGroupRow>(
  persisted: readonly T[] | null | undefined,
  live: readonly T[] | null | undefined,
): T[] {
  const persistedRows = Array.isArray(persisted) ? persisted : [];
  const liveRows = Array.isArray(live) ? live : [];
  if (liveRows.length === 0) return persistedRows.slice();

  const liveByIdentity = new Map<string, T>();
  for (const row of liveRows) {
    const identity = incidentGroupIdentity(row);
    if (!identity || liveByIdentity.has(identity)) continue;
    liveByIdentity.set(identity, row);
  }

  const seen = new Set<string>();
  const merged: T[] = [];
  for (const row of persistedRows) {
    const identity = incidentGroupIdentity(row);
    if (identity) {
      if (seen.has(identity)) continue;
      seen.add(identity);
    }
    const liveRow = identity ? liveByIdentity.get(identity) : undefined;
    merged.push(liveRow ? mergeGroupedReviewFields(row, liveRow) : row);
  }
  return merged;
}

export function reviewQueueFailureUserMessage(kind: ReviewQueueFailureKind): string {
  switch (kind) {
    case 'query_timeout':
      return 'Review Queue query timed out (backend busy). Showing last usable results when available.';
    case 'backend_degraded':
      return 'Review Queue backend degraded (database capacity). Showing last usable results when available.';
    case 'network':
      return 'Network error loading Review Queue. Showing last usable results when available.';
    case 'unavailable':
      return 'Persisted Review Queue unavailable; trying bounded live grouping.';
    default:
      return 'Review Queue failed to load. Showing last usable results when available.';
  }
}
